import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { trackPageView } from './lib/analytics';
import { ProfileProvider } from './contexts/ProfileContext';
import { TeamProvider } from './contexts/TeamContext';
import { PracticeProvider } from './contexts/PracticeContext';
import { DevTools } from './components/DevTools';
import { ProtectedRoute } from './components/ProtectedRoute';
import { BottomNav } from './components/BottomNav';
import { InstallPrompt } from './components/InstallPrompt';
import { ViewportManager } from './components/ViewportManager';
import { LoadingSpinner } from './components/LoadingSpinner';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { AppUpdatePrompt } from './components/AppUpdatePrompt';
import { NotificationsProvider } from './contexts/NotificationsContext';
const Notifications = lazy(() => import('./pages/Notifications').then(module => ({ default: module.Notifications })));

const Home = lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const Login = lazy(() => import('./pages/Login').then(module => ({ default: module.Login })));
const SignUp = lazy(() => import('./pages/SignUp').then(module => ({ default: module.SignUp })));
const CreateProfile = lazy(() => import('./pages/CreateProfile').then(module => ({ default: module.CreateProfile })));
const SelectProfile = lazy(() => import('./pages/SelectProfile').then(module => ({ default: module.SelectProfile })));
const PlayerRoster = lazy(() => import('./pages/PlayerRoster').then(module => ({ default: module.PlayerRoster })));
const QuickScore = lazy(() => import('./pages/QuickScore').then(module => ({ default: module.QuickScore })));
const AllPlayers = lazy(() => import('./pages/AllPlayers').then(module => ({ default: module.AllPlayers })));
const PlayerProfile = lazy(() => import('./pages/PlayerProfile').then(module => ({ default: module.PlayerProfile })));
const PracticeActive = lazy(() => import('./pages/PracticeActive').then(module => ({ default: module.PracticeActive })));
const PracticeHistory = lazy(() => import('./pages/PracticeHistory').then(module => ({ default: module.PracticeHistory })));
const TeamDashboard = lazy(() => import('./pages/TeamDashboard').then(module => ({ default: module.TeamDashboard })));
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Settings = lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })));
const Privacy = lazy(() => import('./pages/Privacy').then(module => ({ default: module.Privacy })));
const DeleteMyData = lazy(() => import('./pages/DeleteMyData').then(module => ({ default: module.DeleteMyData })));
const JoinTeam = lazy(() => import('./pages/JoinTeam').then(module => ({ default: module.JoinTeam })));
const FollowPlayer = lazy(() => import('./pages/FollowPlayer').then(module => ({ default: module.FollowPlayer })));
const JoinEvent = lazy(() => import('./pages/JoinEvent').then(module => ({ default: module.JoinEvent })));
const LiveLeaderboard = lazy(() => import('./pages/LiveLeaderboard').then(module => ({ default: module.LiveLeaderboard })));
const LiveEvents = lazy(() => import('./pages/LiveEvents').then(module => ({ default: module.LiveEvents })));
const ClaimPlayer = lazy(() => import('./pages/ClaimPlayer').then(module => ({ default: module.ClaimPlayer })));
const AthleteSetup = lazy(() => import('./pages/AthleteSetup').then(module => ({ default: module.AthleteSetup })));

const PUBLIC_ROUTES = ['/login', '/signup', '/privacy', '/delete-my-data', '/join', '/join-event', '/leaderboard', '/about', '/follow'];

function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  return null;
}

function ConditionalBottomNav() {
  const location = useLocation();
  const { user } = useAuth();
  const isPublicRoute = PUBLIC_ROUTES.some(r => location.pathname.startsWith(r));
  if (!user || isPublicRoute || location.pathname === '/practice/active' || location.pathname === '/athlete-setup') return null;
  return <BottomNav />;
}

function ConditionalInstallPrompt() {
  const location = useLocation();
  const { user } = useAuth();
  const isPublicRoute = PUBLIC_ROUTES.some(r => location.pathname.startsWith(r));
  if (!user || isPublicRoute || location.pathname === '/practice/active' || location.pathname === '/athlete-setup') return null;
  return <InstallPrompt />;
}

function App() {
  // Pick up invite code from email verification redirect URL and persist to localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pendingInvite = params.get('pending_invite');
    if (pendingInvite) {
      localStorage.setItem('pendingInvite', JSON.stringify({ code: pendingInvite }));
      params.delete('pending_invite');
    }
    const pendingFollow = params.get('pending_follow');
    if (pendingFollow) {
      localStorage.setItem('pendingFollowCode', JSON.stringify({ code: pendingFollow }));
      params.delete('pending_follow');
    }
    if (pendingInvite || pendingFollow) {
      // Clean the params from URL without triggering navigation
      const remaining = params.toString();
      const newUrl = remaining
        ? `${window.location.pathname}?${remaining}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);
  return (
    <BrowserRouter>
      <ViewportManager />
      <RouteChangeTracker />
      <FeatureFlagsProvider>
        <AuthProvider>
          <NotificationsProvider>
          <ProfileProvider>
            <TeamProvider>
              <PracticeProvider>
              <Suspense fallback={<LoadingSpinner fullPage message="Loading DeadBird..." />}>
              <Routes>
                {/* Public routes */}
                <Route path="/about" element={<About />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/delete-my-data" element={<DeleteMyData />} />
                <Route path="/join" element={<JoinTeam />} />
                <Route path="/follow" element={<FollowPlayer />} />
                <Route path="/join-event" element={<JoinEvent />} />
                <Route path="/leaderboard/:eventId" element={<LiveLeaderboard />} />

                {/* Protected routes */}
                <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/live" element={<ProtectedRoute><LiveEvents /></ProtectedRoute>} />
                <Route path="/select-profile" element={<ProtectedRoute><SelectProfile /></ProtectedRoute>} />
                <Route path="/create-profile" element={<ProtectedRoute><CreateProfile /></ProtectedRoute>} />
                <Route path="/start-event" element={<ProtectedRoute><PlayerRoster /></ProtectedRoute>} />
                <Route path="/quick-score" element={<ProtectedRoute><QuickScore /></ProtectedRoute>} />
                <Route path="/players" element={<ProtectedRoute coachOnly><AllPlayers /></ProtectedRoute>} />
                <Route path="/player/:playerId" element={<ProtectedRoute><PlayerProfile /></ProtectedRoute>} />
                <Route path="/practice/active" element={<ProtectedRoute><PracticeActive /></ProtectedRoute>} />
                <Route path="/practice/history" element={<ProtectedRoute><PracticeHistory /></ProtectedRoute>} />
                <Route path="/team-dashboard" element={<ProtectedRoute coachOnly><TeamDashboard /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                <Route path="/claim" element={<ProtectedRoute><ClaimPlayer /></ProtectedRoute>} />
                <Route path="/athlete-setup" element={<ProtectedRoute><AthleteSetup /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
              <ConditionalBottomNav />
              <ConditionalInstallPrompt />
              <AppUpdatePrompt />
              <DevTools />
              </PracticeProvider>
            </TeamProvider>
          </ProfileProvider>
          </NotificationsProvider>
        </AuthProvider>
      </FeatureFlagsProvider>
    </BrowserRouter>
  );
}

export default App;
